/**
 * Item Form Fields - Main Orchestrator
 *
 * Renders the appropriate field components based on item type.
 * Used by both ItemEditor (3-column) and SubItemEditor (2-column).
 */

import { CommonFields } from './CommonFields';
import { SchedulableFields } from './SchedulableFields';
import { StatusField } from './StatusField';
import { TransactionFields } from './TransactionFields';
import { NoteFields } from './NoteFields';
import { UrlsField } from './UrlsField';
import { RemindersField } from './RemindersField';
import { isSchedulableType, hasUrls } from '@perfin/shared';
import type { UseFormReturn } from 'react-hook-form';
import type { ItemType, Reminder } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

export interface ItemFormFieldsProps {
  type: ItemType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  disabled?: boolean;
  fullWebPageHTML?: string;
  sourceUrl?: string;
  createdAtDisplay?: string;
  aiSummary?: import('@perfin/shared').AiSummary;
}

// Combined form data interface for all field types
export interface ItemFormData {
  // Common fields
  name: string;
  description: string;
  category: string;
  subCategory: string;
  itemGroup: string; // Items only (not sub-items)
  owner: string;
  priority: string;

  // Schedulable fields
  dueDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  endDateForever: boolean;
  scheduleLater: boolean;
  recurrence: string;
  daysOfWeek: number[];
  daysOfMonth: (number | string)[];
  datesOfYear: string[];

  // Status field
  status: string;

  // Transaction fields
  transactionType: string;
  amount: string;
  currency: string;
  entityId: string;
  accountId: string;

  // Note fields
  content: string;
  color: string;
  tags: string[];

  // Task-specific
  choreWheel: boolean;

  // Effort time (all types)
  effortHours: number;

  // URLs
  urls: string[];

  // Reminders
  reminders: Reminder[];
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export function getDefaultFormValues(type: ItemType): ItemFormData {
  return {
    // Common
    name: '',
    description: '',
    category: 'None',
    subCategory: 'None',
    itemGroup: 'None',
    owner: 'None',
    priority: 'medium',

    // Schedulable
    dueDate: new Date(),
    startDate: new Date(),
    endDate: new Date(),
    endDateForever: false,
    scheduleLater: false,
    recurrence: 'none',
    daysOfWeek: [],
    daysOfMonth: [],
    datesOfYear: [],

    // Status
    status: type === 'task' ? 'new' : 'draft',

    // Transaction
    transactionType: 'expense',
    amount: '',
    currency: 'USD',
    entityId: 'None',
    accountId: 'None',

    // Note
    content: '',
    color: 'default',
    tags: [],

    // Task-specific
    choreWheel: false,

    // Effort time (default 15 min = 0.25 hr)
    effortHours: 0.25,

    // URLs
    urls: [],

    // Reminders
    reminders: [],
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ItemFormFields({
  type,
  form,
  disabled = false,
  fullWebPageHTML,
  sourceUrl,
  createdAtDisplay,
  aiSummary,
}: ItemFormFieldsProps) {
  const isSchedulable = isSchedulableType(type);
  const supportsUrls = hasUrls(type);

  // Watch recurrence to pass to StatusField
  const recurrence = form.watch('recurrence');

  return (
    <div className="space-y-6">
      {/* Common Fields - All types */}
      <CommonFields
        form={form}
        type={type}
        disabled={disabled}
      />

      {/* Schedulable Fields - Task, Transaction, KeyDate, Note (note only gets dueDate) */}
      {isSchedulable && (
        <SchedulableFields
          form={form}
          disabled={disabled}
          dateLabel={type === 'keyDate' ? 'Date' : 'Due Date'}
          hideRecurrence={type === 'note'}
        />
      )}

      {/* Status Field - All item types (hidden for recurring items) */}
      <StatusField
        form={form}
        type={type}
        disabled={disabled}
        recurrence={recurrence}
      />

      {/* Transaction-Specific Fields */}
      {type === 'transaction' && (
        <TransactionFields
          form={form}
          disabled={disabled}
        />
      )}

      {/* Note-Specific Fields */}
      {type === 'note' && (
        <NoteFields
          form={form}
          disabled={disabled}
          fullWebPageHTML={fullWebPageHTML}
          sourceUrl={sourceUrl}
          createdAtDisplay={createdAtDisplay}
          aiSummary={aiSummary}
        />
      )}

      {/* URLs Field - Task, Transaction, Note (not KeyDate) */}
      {supportsUrls && (
        <UrlsField
          form={form}
          disabled={disabled}
        />
      )}

      {/* Reminders Field - All types */}
      <RemindersField
        form={form}
        disabled={disabled}
      />
    </div>
  );
}

// ============================================================================
// RE-EXPORTS
// ============================================================================

export { CommonFields } from './CommonFields';
export { SchedulableFields } from './SchedulableFields';
export { StatusField } from './StatusField';
export { TransactionFields } from './TransactionFields';
export { NoteFields } from './NoteFields';
export { UrlsField } from './UrlsField';
export { RemindersField } from './RemindersField';
export { AttachmentsField } from './AttachmentsField';

// Two-column layout components
export { ItemFormFieldsCol1 } from './ItemFormFieldsCol1';
export { ItemFormFieldsCol2 } from './ItemFormFieldsCol2';
